import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";
import { useIntercom } from "react-use-intercom";

import ProfileBar from "../../Organisms/App Utility Bar/profilebar";

import renderJoinStakeSaver from "../../Organisms/Modals/Stake Saver/join-stake-saver";
import renderRequestVirtualCard from "../../Organisms/Modals/Virtual Card/request-virtual-card";
import renderVerifyToViewVirtualCard from "../../Organisms/Modals/Virtual Card/verify-to-view-virtual-card";
import renderCreditBuilderOptIn from "../../Organisms/Modals/Credit-Builder/credit-builder-opt-in";
import renderAlertConfirmationModal from "../../Organisms/Modals/alert-confirmation-modal";
import renderManagePaymentMethods from "../../Organisms/Modals/Payment Methods/manage-payment-methods";
import renderMyUtilitiesUpsell from "../../Organisms/Modals/MyUtilities/upsell-modal";
import renderApplyInsurance from "../../Organisms/Modals/Insurance/apply-insurance";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import { useModalContext } from "../../Contexts/ModalContext";

import { useConsumerDeals, useStakeDebit } from "../../CustomHooks/Queries";

import { formatCurrency, hasSVDVirtualCard, isSVDDenied, isSVDVerificationRequired } from "../../Utils/helpers";
import { APIQueryKeys, UpdateDealPaymentModalProps } from "../../Utils/constants";

import { ChevronRight, CrossMark, CelebrationConfetti } from "../../Assets/Icons";

import "./deals.scss";
import "./deals-responsive.scss";

export const useConsumerDealsData = () => {
  const consumerDealsAPI = useConsumerDeals();

  const {
    isLoading: isLoadingConsumerDeals,
    isFetching: isFetchingConsumerDeals,
    data: consumerDealsData,
  } = consumerDealsAPI;

  const offersProps = useMemo(() => {
    const offers = consumerDealsData?.data?.data?.offers ?? [];

    const stakeSaverOffer = offers.find((offer) => offer.id === "stake_saver_bundle");
    const stakeCheckingOffer = offers.find((offer) => offer.id === "stake_checking_account");
    const creditBuilderOffer = offers.find((offer) => offer.id === "credit_builder");
    const insuranceOffer = offers.find((offer) => offer.id === "stake_partner_insurance");
    const myUtilitiesOffer = offers.find((offer) => offer.id === "stake_partner_utilities");
    const neighborhoodOffer = offers.find((offer) => offer.id === "neighborhood_network");

    const isStakeSaverActive = stakeSaverOffer?.is_activated ?? false;
    const isStakeCheckingActive = stakeCheckingOffer?.is_activated ?? false;
    const isCreditBuilderActive = creditBuilderOffer?.is_activated ?? false;
    const isInsuranceActive = insuranceOffer?.is_activated ?? false;
    const isMyUtilitiesActive = myUtilitiesOffer?.is_activated ?? false;
    const isNeighborhoodActive = neighborhoodOffer?.is_activated ?? false;

    return {
      offers,
      stakeSaverOffer,
      stakeCheckingOffer,
      creditBuilderOffer,
      insuranceOffer,
      myUtilitiesOffer,
      isStakeSaverActive,
      isStakeCheckingActive,
      isCreditBuilderActive,
      isInsuranceActive,
      isMyUtilitiesActive,
      isNeighborhoodActive,
    };
  }, [consumerDealsData]);

  return {
    isLoadingConsumerDealsData: isLoadingConsumerDeals,
    isFetchingConsumerDealsData: isFetchingConsumerDeals,
    consumerDealsAPI,
    ...offersProps,
  };
};

export const useStakeSaverDealAction = (isStakeSaverActive) => {
  const modalContext = useModalContext();

  const navigate = useNavigate();

  return () => {
    if (isStakeSaverActive) {
      navigate("/deals/stake-saver");
    } else {
      renderJoinStakeSaver(modalContext);
    }
  };
};

export const useStakeCheckingDealAction = (isStakeCheckingActive) => {
  const modalContext = useModalContext();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { show: showIntercom } = useIntercom();

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const { data: stakeDebit } = useStakeDebit();

  return useCallback(
    () => {
      if (isStakeCheckingActive) {
        if (hasSVDVirtualCard(stakeDebit)) {
          renderVerifyToViewVirtualCard(modalContext);
        } else {
          renderRequestVirtualCard(modalContext);
        }
      } else {
        if (isSVDVerificationRequired(stakeDebit?.data)) {
          navigate("/verify-stake-checking", { state: { previousPathname: pathname } });
        } else if (isSVDDenied(stakeDebit?.data)) {
          showIntercom();
        } else {
          stakeCheckingStatusAction(stakeDebit?.data);
        }
      }
    }, //eslint-disable-next-line
    [isStakeCheckingActive, modalContext, stakeDebit, stakeCheckingStatusAction],
  );
};

export const useCreditBuilderDealAction = (isCreditBuilderActive) => {
  const modalContext = useModalContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(
    () => {
      if (isCreditBuilderActive) {
        navigate("/credit-builder");
      } else {
        renderCreditBuilderOptIn(modalContext, {
          onOptIn: () => queryClient.refetchQueries(APIQueryKeys.consumerDeals),
        });
      }
    }, //eslint-disable-next-line
    [isCreditBuilderActive, modalContext],
  );
};

export const useInsuranceDealAction = (isInsuranceActive) => {
  const navigate = useNavigate();

  const modalContext = useModalContext();

  return useCallback(() => {
    if (isInsuranceActive) {
      navigate("/deals/insurance/manage");
    } else {
      renderApplyInsurance(modalContext);
    }
  }, [isInsuranceActive, modalContext, navigate]);
};

export const useMyUtilitiesDealAction = (isActive) => {
  const modalContext = useModalContext();
  const navigate = useNavigate();

  return useCallback(() => {
    if (isActive) {
      navigate("/deals/my-utilities/manage");
    } else {
      renderMyUtilitiesUpsell(modalContext);
    }
  }, [modalContext, isActive, navigate]);
};

export const useNeighbourhoodAction = (isNeighborhoodActive) => {
  const navigate = useNavigate();

  return useCallback(() => {
    navigate("/deals/neighborhood-network");
  }, [navigate]);
};

export const useAttributesInjectedOffers = () => {
  const {
    offers,
    isStakeSaverActive,
    isStakeCheckingActive,
    isCreditBuilderActive,
    isInsuranceActive,
    isMyUtilitiesActive,
    isNeighborhoodActive,
  } = useConsumerDealsData();

  const StakeSaverDealAction = useStakeSaverDealAction(isStakeSaverActive);
  const StakeCheckingDealAction = useStakeCheckingDealAction(isStakeCheckingActive);
  const CreditBuilderDealAction = useCreditBuilderDealAction(isCreditBuilderActive);
  const InsuranceDealAction = useInsuranceDealAction(isInsuranceActive);
  const MyUtilitiesDealAction = useMyUtilitiesDealAction(isMyUtilitiesActive);
  const NeighborhoodAction = useNeighbourhoodAction(isNeighborhoodActive);

  const attributesInjectedOffers = useMemo(() => {
    const getOfferAction = (offer) => {
      switch (offer.id) {
        case "stake_saver_bundle":
          return StakeSaverDealAction;
        case "stake_checking_account":
          return StakeCheckingDealAction;
        case "credit_builder":
          return CreditBuilderDealAction;
        case "stake_partner_insurance":
          return InsuranceDealAction;
        case "stake_partner_utilities":
          return MyUtilitiesDealAction;
        case "neighborhood_network":
          return NeighborhoodAction;
        default:
          return () => {};
      }
    };

    return offers.map((offer) => ({ ...offer, onButtonClick: getOfferAction(offer) }));
  }, [
    offers,
    StakeSaverDealAction,
    StakeCheckingDealAction,
    CreditBuilderDealAction,
    InsuranceDealAction,
    MyUtilitiesDealAction,
    NeighborhoodAction,
  ]);

  return attributesInjectedOffers;
};

const DealCard = ({
  className = "",
  icon,
  title,
  subtitle,
  description,
  image_link,
  resident_facing_cta,
  is_activated,
  status_display_text,
  status_badge_type,
  onButtonClick,
}) => {
  return (
    <div className={`deal-card ${className}`}>
      <div className="deal-icon">{!!image_link ? <img src={image_link} alt="" /> : icon}</div>
      <div className="deal-title">{title}</div>
      {false && <div className="deal-reward">{subtitle}</div>}
      <div className="deal-description">{description}</div>
      {!!onButtonClick && (
        <>
          <div className="spacer"></div>
          <button className="deal-action-button" onClick={onButtonClick} type="button">
            {resident_facing_cta}
            <ChevronRight />
          </button>
        </>
      )}
      {!!status_display_text ? (
        <span className={`deal-badge ${status_badge_type}`}>{status_display_text}</span>
      ) : is_activated ? (
        <span className="deal-badge success">Activated</span>
      ) : (
        <></>
      )}
    </div>
  );
};

export default function Deals() {
  const [dealPaymentFailure, setDealPaymentFailure] = useState(null);
  const [UMFRebateInfo, setUMFRebateInfo] = useState(null);
  const [stakeSaverDiscount, setStakeSaverDiscount] = useState(null);

  const modalContext = useModalContext();
  const { modalVisibility, onModalClose } = modalContext;

  const { isLoading: isLoadingStakeDebit } = useStakeDebit();

  const {
    isLoadingConsumerDealsData,
    offers,
    stakeSaverOffer,
    isStakeSaverActive,
    consumerDealsAPI: { data: consumerDealsAPIData },
  } = useConsumerDealsData();

  useEffect(() => {
    if (stakeSaverOffer?.offers_notification?.is_payment_failed) {
      const {
        offer_name,
        id,
        offers_notification,
        check_out_details: { selected_payment_method },
      } = stakeSaverOffer;
      setDealPaymentFailure({ offer_name, id, selected_payment_method, ...offers_notification });
    }
  }, [stakeSaverOffer]);

  useEffect(() => {
    if (consumerDealsAPIData?.data?.data?.rebate_information?.is_rebate_not_available) {
      setUMFRebateInfo(consumerDealsAPIData?.data?.data?.rebate_information);
    }
  }, [consumerDealsAPIData?.data?.data?.rebate_information]);

  useEffect(() => {
    if (stakeSaverOffer?.is_activated && !!stakeSaverOffer?.check_out_details?.activatedFrequency?.[0]?.discount) {
      setStakeSaverDiscount(stakeSaverOffer?.check_out_details?.activatedFrequency?.[0]?.discount);
    }
  }, [stakeSaverOffer]);

  useEffect(() => {
    if (!modalVisibility && !!stakeSaverOffer && !isStakeSaverActive && offers?.length === 1) {
      renderJoinStakeSaver(modalContext);
    }
  }, [modalContext, modalVisibility, stakeSaverOffer, isStakeSaverActive, offers]);

  const attributesInjectedOffers = useAttributesInjectedOffers();

  const deals = useMemo(
    () => [...attributesInjectedOffers.filter((offer) => !offer.parent_id && !offer.is_activated)],

    [attributesInjectedOffers],
  );

  const stakeSaverDeals = useMemo(
    () => attributesInjectedOffers.filter((offer) => offer.parent_id === "stake_saver_bundle"),
    [attributesInjectedOffers],
  );

  const activatedDeals = useMemo(
    () => [...attributesInjectedOffers.filter((offer) => !offer.parent_id && offer.is_activated && offer.id)],
    [attributesInjectedOffers],
  );

  const isLoading = isLoadingConsumerDealsData || isLoadingStakeDebit;

  const handleUpdatePayment = () => {
    renderAlertConfirmationModal(modalContext, {
      ...UpdateDealPaymentModalProps,
      description: dealPaymentFailure?.payment_failure_description,
      onCTAButtonClick: () =>
        renderManagePaymentMethods(modalContext, {
          selectedPaymentMethod: dealPaymentFailure?.selected_payment_method,
          mustUpdatePaymentMethod: true,
          chargePaymentImmediately: true,
          dealId: dealPaymentFailure?.id,
        }),
      onSecondaryCTAButtonClick: onModalClose,
    });
  };

  return (
    <div className="deals">
      <section>
        <div className="max-width-container">
          <ProfileBar />
          {isLoading && (
            <div className="loading-content">
              <CircularProgress />
            </div>
          )}
          {!isLoading && (
            <>
              {!!dealPaymentFailure && (
                <div className="deal-banner alert mb-6">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <h4 className="title">Payment Failed</h4>
                    <button onClick={setDealPaymentFailure.bind(this, null)} className="close-button">
                      <CrossMark />
                    </button>
                  </div>
                  <p className="description">Update your payment method to keep {dealPaymentFailure?.offer_name}.</p>
                  <button className="action-button" onClick={handleUpdatePayment}>
                    Update Now
                  </button>
                </div>
              )}
              {!!UMFRebateInfo && (
                <div className="deal-banner success mb-6">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <h4 className="title">{UMFRebateInfo?.rebate_not_available_msg}</h4>
                  </div>
                </div>
              )}
              {!!stakeSaverDiscount && (
                <div className="deal-banner success mb-6">
                  <div className="d-flex align-items-start gap-4">
                    <CelebrationConfetti className="banner-icon" />
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <h4 className="title">
                          You just saved {formatCurrency(stakeSaverDiscount).replace(".00", "")} by joining Stake Saver!
                        </h4>
                        <button onClick={setStakeSaverDiscount.bind(this, null)} className="close-button">
                          <CrossMark />
                        </button>
                      </div>
                      <p className="description mb-0">Keep earning rewards and building credit.</p>
                    </div>
                  </div>
                </div>
              )}
              {deals.length > 0 && (
                <>
                  <h3 className="deals-group-title">Let's use some of that cash...</h3>
                  <Row className="deals-group" md={2} xs={1}>
                    {deals.map((deal, idx) => {
                      return (
                        <Col key={idx}>
                          <DealCard {...deal} />
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
              {stakeSaverDeals.length > 0 && (
                <>
                  <h3 className="deals-group-title">{isStakeSaverActive ? "Stake Saver" : "More Deals"}</h3>
                  <Row className="deals-group" md={2} xs={1}>
                    {stakeSaverDeals.map((deal, idx) => {
                      return (
                        <Col key={idx}>
                          <DealCard {...deal} />
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
              {activatedDeals.length > 0 && (
                <>
                  <h3 className="deals-group-title">Activated Deals</h3>
                  <Row className="deals-group" md={2} xs={1}>
                    {activatedDeals.map((deal, idx) => {
                      return (
                        <Col key={idx}>
                          <DealCard {...deal} />
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
